import React, { useState } from "react";
import * as Mui from "@mui/material";
import { ActiveListingModifyResource, UpdateActiveListingModifyResource } from "api/resource/active-listing-modify";
import * as Icons from "react-feather";
import { RichTextEditor } from "component/shared/rich-text-editor";
import { FullPageCard } from "component/shared/full-page-card";
import { FeatherIcon } from "component/shared/feather-icon";

interface Props {
	listing: ActiveListingModifyResource | null;
	saving: boolean;
	onSave: (body: UpdateActiveListingModifyResource) => void;
	onCancel: () => void;
}

interface FormState {
	propDescription: string;
	tourYn: boolean;
	tourUrl: string;
	tourLink: string;
	openYn: boolean;
	openDateText: string;
	openDate: string;
	openTime: string;
	openAgent: string;
	openHomeDirections: string;
	openHomeRemarks: string;
	mapYn: boolean;
	extraLinkYn: boolean;
	extraLinkUrl: string;
	extraLinkText: string;
}

// The API carries OpenDate as CF's mm/dd/yyyy; the <input type="date"> needs yyyy-mm-dd. Convert on the
// boundary in both directions and leave anything unparseable blank rather than guessing.
function apiToInputDate(value: string | null): string {
	if (!value) {
		return "";
	}
	const parts = value.split("/");
	if (parts.length !== 3) {
		return "";
	}
	const [mm, dd, yyyy] = parts;
	if (!mm || !dd || !yyyy) {
		return "";
	}
	return `${yyyy.padStart(4, "0")}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
}

function inputToApiDate(value: string): string | null {
	if (!value) {
		return null;
	}
	const parts = value.split("-");
	if (parts.length !== 3) {
		return null;
	}
	const [yyyy, mm, dd] = parts;
	return `${mm}/${dd}/${yyyy}`;
}

function todayInputDate(): string {
	const now = new Date();
	const yyyy = String(now.getFullYear());
	const mm = String(now.getMonth() + 1).padStart(2, "0");
	const dd = String(now.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}

// ReactQuill normalizes plain-text MLS remarks to HTML (<p>…</p>, trailing newlines) on mount, so a raw
// string compare against the MLS default gives false positives. Compare on text content instead.
function htmlToText(html: string): string {
	return (html || "")
		.replace(/<[^>]*>/g, "")
		.replace(/&nbsp;/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

function initialState(listing: ActiveListingModifyResource | null): FormState {
	if (listing) {
		return {
			// Seed the editor with the saved override if present, else the live MLS remarks so the agent
			// edits from the real text. handleSave only persists an override when it differs from the MLS
			// default (see resolveDescription), so an unedited seed keeps tracking the feed.
			propDescription:
				listing.propDescription && listing.propDescription.trim()
					? listing.propDescription
					: listing.defaultDescription || "",
			tourYn: !!listing.tourYn,
			tourUrl: listing.tourUrl || "",
			// CF seeds the default display text into the input VALUE (listingsmodify_output_1.cfm:179,332),
			// so an unedited save persists it — seed the form state, don't use a placeholder.
			tourLink: listing.tourLink || "Virtual Tour",
			openYn: !!listing.openYn,
			openDateText: listing.openDateText || "",
			// CF pre-fills OpenDate with today when the row has none (listingsmodify_output_1.cfm:219).
			openDate: apiToInputDate(listing.openDate) || todayInputDate(),
			openTime: listing.openTime || "",
			openAgent: listing.openAgent || "",
			openHomeDirections: listing.openHomeDirections || "",
			openHomeRemarks: listing.openHomeRemarks || "",
			mapYn: listing.mapYn == null ? true : !!listing.mapYn,
			extraLinkYn: !!listing.extraLinkYn,
			extraLinkUrl: listing.extraLinkUrl || "",
			extraLinkText: listing.extraLinkText || "Click Here!",
		};
	}
	return {
		propDescription: "",
		tourYn: false,
		tourUrl: "",
		tourLink: "Virtual Tour",
		openYn: false,
		openDateText: "",
		openDate: todayInputDate(),
		openTime: "",
		openAgent: "",
		openHomeDirections: "",
		openHomeRemarks: "",
		mapYn: true,
		extraLinkYn: false,
		extraLinkUrl: "",
		extraLinkText: "Click Here!",
	};
}

export const ActiveListingForm: React.FC<Props> = ({ listing, saving, onSave, onCancel }) => {
	const [form, setForm] = useState<FormState>(() => initialState(listing));
	// Bumped to remount the RichTextEditor on Reset — Quill won't re-sync from a changed value prop once
	// its internal state is populated.
	const [descriptionKey, setDescriptionKey] = useState(0);

	const defaultDescription = (listing && listing.defaultDescription) || "";
	// True while the editor still shows the live MLS remarks (seeded/untouched, or after Reset) — compared
	// on text content since Quill re-wraps the seed in HTML on mount.
	const usingMlsDefault =
		!!defaultDescription && htmlToText(form.propDescription) === htmlToText(defaultDescription);

	const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
		setForm((prev) => ({ ...prev, [key]: value }));
	};

	// Reset the description back to the live MLS default. Persisting this clears the override (see
	// resolveDescription), so the listing resumes showing the feed's remarks.
	const resetDescription = () => {
		set("propDescription", defaultDescription);
		setDescriptionKey((k) => k + 1);
	};

	// Only persist propDescription when it differs from the live MLS remarks. Matching the default (the
	// seeded/untouched state, or after Reset) stores null so the public page keeps showing the live feed.
	const resolveDescription = (): string | null => {
		if (usingMlsDefault) {
			return null;
		}
		return form.propDescription || null;
	};

	const switchField = (label: string, key: keyof FormState) => (
		<Mui.FormControlLabel
			control={
				<Mui.Switch
					color="primary"
					checked={!!(form as any)[key]}
					onChange={(e) => set(key as any, e.target.checked as any)}
				/>
			}
			label={label}
		/>
	);

	const handleSave = () => {
		const body: UpdateActiveListingModifyResource = {
			propDescription: resolveDescription(),
			tourYn: form.tourYn,
			tourUrl: form.tourUrl || null,
			tourLink: form.tourLink || null,
			openYn: form.openYn,
			openDateText: form.openDateText || null,
			openDate: inputToApiDate(form.openDate),
			openTime: form.openTime || null,
			openAgent: form.openAgent || null,
			openHomeDirections: form.openHomeDirections || null,
			openHomeRemarks: form.openHomeRemarks || null,
			mapYn: form.mapYn,
			extraLinkYn: form.extraLinkYn,
			extraLinkUrl: form.extraLinkUrl || null,
			extraLinkText: form.extraLinkText || null,
		};
		onSave(body);
	};

	// Each section renders as its own card (emulating /settings/listing-details), matching the
	// Supplemental / Off-Market editors.
	const sectionCard = (content: React.ReactNode) => (
		<Mui.Box mb={2}>
			<FullPageCard>
				<Mui.Grid container direction="column" spacing={2} style={{ maxWidth: 560 }}>
					{content}
				</Mui.Grid>
			</FullPageCard>
		</Mui.Box>
	);

	return (
		<>
			{sectionCard(
				<>
					<Mui.Grid item>
						<Mui.Typography variant="h6">Property Description</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item>
						{/* Reset lives in the editor toolbar; shown only when a custom description is in effect
						    (not while the editor still displays the live MLS remarks). */}
						<RichTextEditor
							key={descriptionKey}
							toolbarId="active-desc-toolbar"
							value={form.propDescription}
							onChange={(value: string) => set("propDescription", value)}
							onReset={defaultDescription && !usingMlsDefault ? resetDescription : undefined}
							resetTitle="Reset to MLS default"
						/>
					</Mui.Grid>
					{defaultDescription && (
						<Mui.Grid item>
							<Mui.Box display="flex" alignItems="flex-start">
								<FeatherIcon fontSize="small">
									<Icons.Info />
								</FeatherIcon>
								<Mui.Typography variant="caption" color="textSecondary" style={{ marginLeft: 6 }}>
									A saved description replaces the MLS remarks on your listing's public page.
								</Mui.Typography>
							</Mui.Box>
						</Mui.Grid>
					)}
				</>
			)}

			{sectionCard(
				<>
					<Mui.Grid item>
						<Mui.Typography variant="h6">Map Options</Mui.Typography>
					</Mui.Grid>
					<Mui.Grid item>{switchField("Show Map", "mapYn")}</Mui.Grid>
				</>
			)}

			{/* Virtual Tour, Extra Link, and Open House share one "Other Settings" card — matching the
			    Supplemental / Off-Market editors. */}
			{sectionCard(
				<>
					<Mui.Grid item>
						<Mui.Typography variant="h6">Other Settings</Mui.Typography>
					</Mui.Grid>

					<Mui.Grid item data-testid="active-tour-toggle">{switchField("Show Virtual Tour", "tourYn")}</Mui.Grid>
					{form.tourYn && (
						<>
							<Mui.Grid item>
								<Mui.TextField
									fullWidth label="Tour URL" variant="outlined" size="small"
									inputProps={{ maxLength: 1024, "data-testid": "active-tour-url" }}
									value={form.tourUrl} onChange={(e) => set("tourUrl", e.target.value)}
								/>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.TextField
									fullWidth label="Tour Link Text" variant="outlined" size="small"
									InputLabelProps={{ shrink: true }}
									inputProps={{ maxLength: 64 }}
									value={form.tourLink} onChange={(e) => set("tourLink", e.target.value)}
								/>
							</Mui.Grid>
						</>
					)}

					<Mui.Grid item data-testid="active-extralink-toggle">{switchField("Show Extra Link", "extraLinkYn")}</Mui.Grid>
					{form.extraLinkYn && (
						<>
							<Mui.Grid item>
								<Mui.TextField
									fullWidth label="Extra Link URL" variant="outlined" size="small"
									inputProps={{ maxLength: 128, "data-testid": "active-extra-url" }}
									value={form.extraLinkUrl} onChange={(e) => set("extraLinkUrl", e.target.value)}
								/>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.TextField
									fullWidth label="Extra Link Text" variant="outlined" size="small"
									InputLabelProps={{ shrink: true }}
									inputProps={{ maxLength: 64 }}
									value={form.extraLinkText} onChange={(e) => set("extraLinkText", e.target.value)}
								/>
							</Mui.Grid>
						</>
					)}

					<Mui.Grid item data-testid="active-open-toggle">{switchField("Show Open House", "openYn")}</Mui.Grid>
					{form.openYn && (
						<>
							<Mui.Grid item>
								<Mui.TextField
									fullWidth label="Open House Text" variant="outlined" size="small"
									inputProps={{ maxLength: 99 }}
									value={form.openDateText} onChange={(e) => set("openDateText", e.target.value)}
								/>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.Grid container spacing={2}>
									<Mui.Grid item xs={6}>
										<Mui.TextField
											fullWidth label="Open House Date" type="date" variant="outlined" size="small"
											InputLabelProps={{ shrink: true }}
											inputProps={{ "data-testid": "active-open-date" }}
											value={form.openDate} onChange={(e) => set("openDate", e.target.value)}
										/>
									</Mui.Grid>
									<Mui.Grid item xs={6}>
										<Mui.TextField
											fullWidth label="Open House Time" variant="outlined" size="small"
											inputProps={{ maxLength: 50 }}
											value={form.openTime} onChange={(e) => set("openTime", e.target.value)}
										/>
									</Mui.Grid>
								</Mui.Grid>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.TextField
									fullWidth label="Open House Agent" variant="outlined" size="small"
									inputProps={{ maxLength: 50 }}
									value={form.openAgent} onChange={(e) => set("openAgent", e.target.value)}
								/>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.TextField
									fullWidth multiline minRows={2} label="Directions" variant="outlined" size="small"
									value={form.openHomeDirections} onChange={(e) => set("openHomeDirections", e.target.value)}
								/>
							</Mui.Grid>
							<Mui.Grid item>
								<Mui.TextField
									fullWidth multiline minRows={2} label="Remarks" variant="outlined" size="small"
									value={form.openHomeRemarks} onChange={(e) => set("openHomeRemarks", e.target.value)}
								/>
							</Mui.Grid>
						</>
					)}
				</>
			)}

			{sectionCard(
				<Mui.Grid item>
					<Mui.Grid container spacing={2}>
						<Mui.Grid item>
							<Mui.Button
								variant="contained" color="primary"
								onClick={handleSave}
								disabled={saving}
								data-testid="active-save"
							>
								Save Listing
							</Mui.Button>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Button variant="outlined" onClick={onCancel} disabled={saving}>
								Cancel
							</Mui.Button>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Grid>
			)}
		</>
	);
};
